import { useEffect, useState } from "react"

import { Settings2 } from "lucide-react"
import { ToggleOptionGroup, ToggleOptionGroupItem, DefaultTooltip } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../../utils"
import AdvancedSize from "../AdvancedSize"

import Container from "./Container"
import Item from "./Item"
import Section from "./Section"
import Size from "./Size"
import { FrameProvider, useFrame } from "./context"
import { parseSizeToTailwind, parseSpaceToTailwind, parseTailwindToSize, parseSpaceString } from "./helpers"

const FrameProperty = () => {
  const [advancedSize, setAdvancedSize] = useState()
  const { frame: { sizeTab = 'standard' } } = useFrame()
  const sizeKey = sizeTab === 'standard' ? '' : `${sizeTab}.`

  const { rawValue: min } = useCraftProp('size.min')
  const { rawValue: max } = useCraftProp('size.max')

  const { value: width = '', setValue: setWidth } = useCraftProp(`size.${sizeKey}width`)
  const { value: height = '', setValue: setHeight } = useCraftProp(`size.${sizeKey}height`)

  const { value: margin = '', setValue: setMargin } = useCraftProp('space.margin')
  const { value: padding = '', setValue: setPadding } = useCraftProp('space.padding')

  const setSpace = {
    margin: setMargin,
    padding: setPadding,
  }

  const setSize = {
    width: setWidth,
    height: setHeight,
  }

  const handleSizeChange = ({ size: newSize, type }) => {
    const tailwindClass = parseSizeToTailwind({ size: newSize, type })
    setSize[type](tailwindClass)
  }

  const handleSpaceChange = ({ type, space: newSpace }) => {
    const tailwindClasses = parseSpaceToTailwind({ spaces: newSpace, type })
    setSpace[type](tailwindClasses)
  }

  useEffect(() => {
    if (advancedSize === undefined) {
      setAdvancedSize(!!(min || max))
    }
  }, [min, max, advancedSize, setAdvancedSize])

  return <>
    <div className='relative'>
      <ToggleOptionGroup className='absolute right-0 -top-8 p-px mt-px' value={advancedSize ? 'advanced' : ''} type='single' onValueChange={() => setAdvancedSize(!advancedSize)}>
        <ToggleOptionGroupItem value='advanced'>
          <DefaultTooltip side='left' content='Enable advanced size' triggerProps={{ className: 'w-full flex justify-center' }}>
            <Settings2 size='16' />
          </DefaultTooltip>
        </ToggleOptionGroupItem>
      </ToggleOptionGroup>
      <Frame
        onSizeChange={handleSizeChange}
        onSpaceChange={handleSpaceChange}
        size={{ width, height }}
        space={{ margin, padding }}
      />
      <AdvancedSize show={advancedSize} />
    </div>
  </>
}

const Frame = ({ onSizeChange, onSpaceChange, size: initialSize, space: initialSpace }) => {
  const [spaceType, setSpaceType] = useState({ margin: 'all', padding: 'all' })
  const [space, setSpace] = useState()
  const [size, setSize] = useState()

  const [editing, setEditing] = useState(false)

  const handleEditing = (value, position, type) => {
    setEditing({ value, position, type })
  }

  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()
  const { frame: { sizeTab } } = useFrame()

  useEffect(() => {
    setSize(null)
    setSpace(null)
  }, [device, selectedUtilities, sizeTab])

  useEffect(() => {
    if (size) {
      return
    }

    setSize({
      width: parseTailwindToSize(initialSize.width, 'width'),
      height: parseTailwindToSize(initialSize.height, 'height'),
    })
  }, [initialSize, size])

  useEffect(() => {
    if (space) {
      return
    }

    const { value: parsedMargin, type: marginType } = parseSpaceString(initialSpace.margin || '', 'margin')
    const { value: parsedPadding, type: paddingType } = parseSpaceString(initialSpace.padding || '', 'padding')

    setSpace({ margin: parsedMargin, padding: parsedPadding })
    setSpaceType({ margin: marginType, padding: paddingType })
  }, [initialSpace, space])

  const handleTypeChange = (type, value) => {
    setSpaceType(prev => ({ ...prev, [type]: value }))
  }

  const handleSizeChange = ({ type, unit, value, predefined }) => {
    setSize(prev => {
      const newSize = { ...prev }
      newSize[type] = { value, unit, predefined }
      onSizeChange({ size: newSize[type], type })
      return newSize
    })
  }

  const handleSpaceChange = ({ type, position, unit, value, predefined }) => {
    setSpace(prev => {
      const newSpace = { ...prev }

      switch (spaceType[type]) {
        case 'individual':
          newSpace[type][position] = { value, unit, predefined }
          break

        case 'axis':
          if (position === 'top' || position === 'bottom') {
            newSpace[type].top = { value, unit, predefined }
            newSpace[type].bottom = { value, unit, predefined }
          } else if (position === 'left' || position === 'right') {
            newSpace[type].left = { value, unit, predefined }
            newSpace[type].right = { value, unit, predefined }
          }
          break

        case 'all':
          Object.keys(newSpace[type]).forEach(pos => {
            newSpace[type][pos] = { value, unit, predefined }
          })
          break
      }

      onSpaceChange({ type, space: newSpace[type] })
      return newSpace
    })
  }

  const spaceToUse = space || SPACE_DEFAULT
  const sizeToUse = size || SIZE_DEFAULT

  return <>
    {spaceToUse && <>
      <Container>
        <Section type={spaceType.margin} onTypeChange={value => handleTypeChange('margin', value)}>
          margin
        </Section>
        <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.margin} config={spaceToUse.margin.top} position='top' type='margin' onSpaceChange={handleSpaceChange} />
        <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.margin} config={spaceToUse.margin.bottom} position='bottom' type='margin' onSpaceChange={handleSpaceChange} />
        <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.margin} config={spaceToUse.margin.left} position='left' type='margin' onSpaceChange={handleSpaceChange} />
        <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.margin} config={spaceToUse.margin.right} position='right' type='margin' onSpaceChange={handleSpaceChange} />
        <Container>
          <Section type={spaceType.padding} onTypeChange={value => handleTypeChange('padding', value)}>
            padding
          </Section>
          <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.padding} config={spaceToUse.padding.top} position='top' type='padding' onSpaceChange={handleSpaceChange} />
          <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.padding} config={spaceToUse.padding.bottom} position='bottom' type='padding' onSpaceChange={handleSpaceChange} />
          <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.padding} config={spaceToUse.padding.left} position='left' type='padding' onSpaceChange={handleSpaceChange} />
          <Item onEditing={handleEditing} editing={editing} spaceType={spaceType.padding} config={spaceToUse.padding.right} position='right' type='padding' onSpaceChange={handleSpaceChange} />
          <Size config={sizeToUse} onSizeChange={handleSizeChange} />
        </Container>
      </Container>
    </>}
  </>
}

const FrameWrapper = () => <>
  <FrameProvider>
    <FrameProperty />
  </FrameProvider>
</>

const SIZE_DEFAULT = {
  width: { value: null, unit: null, predefined: true },
  height: { value: null, unit: null, predefined: true },
}

const SPACE_DEFAULT = {
  margin: { top: { value: null, unit: null, predefined: true }, bottom: { value: null, unit: null, predefined: true }, left: { value: null, unit: null, predefined: true }, right: { value: null, unit: null, predefined: true } },
  padding: { top: { value: null, unit: null, predefined: true }, bottom: { value: null, unit: null, predefined: true }, left: { value: null, unit: null, predefined: true }, right: { value: null, unit: null, predefined: true } },
}

export default FrameWrapper
